<template>
  <transition name="modal" appear>
    <div v-if="visible" class="register-container">
      <div class="register-overlay" @click="goBack"></div>
      <div class="register-box">
      <div class="register-header">
        <h2 class="register-title">注册</h2>
        <button class="close-btn" @click="goBack">
          <i class="fa-solid fa-times"></i>
        </button>
      </div>
      <form @submit.prevent="handleRegister" class="register-form" novalidate>
        <div v-if="bindToken" class="bind-notice-banner">
          <i class="fa-solid fa-circle-info"></i>
          <span>您正在注册新账号，注册成功后将自动绑定扫码的第三方账号并直接登录。</span>
        </div>
        <div class="form-group">
          <label for="username">账号 *</label>
          <input
            type="text"
            id="username"
            v-model="registerForm.username"
            placeholder="6-18位大小写字母和数字"
            required
            :class="{ 'error': errors.username }"
            @blur="validateUsernameOnBlur"
          />
          <span v-if="errors.username" class="error-message">{{ errors.username }}</span>
        </div>

        <div class="form-group">
          <label for="password">密码 *</label>
          <div class="password-input-wrapper">
            <input
              :type="showPassword ? 'text' : 'password'"
              id="password"
              v-model="registerForm.password"
              placeholder="8-16位，包含大小写字母和数字，可含特殊字符"
              required
              :class="{ 'error': errors.password }"
              @blur="validateFieldOnBlur('password')"
            />
            <button type="button" class="toggle-password-btn" @click="showPassword = !showPassword" tabindex="-1">
              <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
            </button>
          </div>
          <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
        </div>

        <div class="form-group">
          <label for="confirmPassword">确认密码 *</label>
          <div class="password-input-wrapper">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              id="confirmPassword"
              v-model="registerForm.confirmPassword"
              placeholder="请再次输入密码"
              required
              :class="{ 'error': errors.confirmPassword }"
              @blur="validateFieldOnBlur('confirmPassword')"
            />
            <button type="button" class="toggle-password-btn" @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
              <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
            </button>
          </div>
          <span v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</span>
        </div>

        <div class="form-group">
          <label for="phone">手机号 *</label>
          <input
            type="tel"
            id="phone"
            v-model="registerForm.phone"
            placeholder="请输入手机号"
            required
            :class="{ 'error': errors.phone }"
            @blur="validateFieldOnBlur('phone')"
          />
          <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
        </div>

        <div class="form-group">
          <label for="email">电子邮箱 *</label>
          <input
            type="email"
            id="email"
            v-model="registerForm.email"
            placeholder="请输入邮箱地址"
            required
            :class="{ 'error': errors.email }"
            @blur="validateFieldOnBlur('email')"
          />
          <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
        </div>

        <div class="form-group">
          <label for="emailCode">邮箱验证码 *</label>
          <div class="code-container">
            <input
              type="text"
              id="emailCode"
              v-model="registerForm.emailCode"
              placeholder="请输入邮箱验证码"
              required
              :class="{ 'error': errors.emailCode }"
            />
            <button
              type="button"
              @click="sendEmailCode"
              :disabled="emailCodeCooldown > 0 || !isValidEmail(registerForm.email)"
              class="code-btn"
            >
              {{ emailCodeCooldown > 0 ? `${emailCodeCooldown}s` : '发送验证码' }}
            </button>
          </div>
          <span v-if="errors.emailCode" class="error-message">{{ errors.emailCode }}</span>
        </div>



        <button type="submit" class="register-btn" :disabled="isLoading">
          {{ isLoading ? '注册中...' : '注册' }}
        </button>

        <div class="form-links">
          <span>已有账号？</span>
          <button @click="switchToLogin" class="login-link">立即登录</button>
        </div>
        
        <!-- 免责声明提示 -->
        <div class="disclaimer-notice">
          <p>注册即代表您已经详细阅读并同意了我们的
            <router-link to="/mianze" class="disclaimer-link" @click="goBack">《免责声明》</router-link>
            和
            <router-link to="/rules" class="disclaimer-link" @click="goBack">《文章规则》</router-link>
          </p>
        </div>
      </form>
      <!-- 安全滑块验证码弹窗 -->
      <SlideCaptcha v-model:visible="captchaVisible" @success="onCaptchaSuccess" />
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, reactive, onUnmounted, watch } from 'vue'
import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'
import { message } from '@/utils/ui-service'
import { useRouter } from 'vue-router'
import { isRateLimitError } from '@/utils/httpErrorUtils.js'
import SlideCaptcha from '@/components/SlideCaptcha.vue'

export default {
  name: 'RegisterPage',
  components: {
    SlideCaptcha
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'switch-to-login'],
  setup(props, { emit }) {
    const isLoading = ref(false)
    const emailCodeCooldown = ref(0)
    const cooldownTimer = ref(null)
    const router = useRouter()
    const captchaVisible = ref(false)
    const showPassword = ref(false)
    const showConfirmPassword = ref(false)
    const verifiedCaptchaToken = ref('')
    const captchaPurpose = ref('register')
    const bindToken = ref(localStorage.getItem('tempBindToken') || '')

    const registerForm = reactive({
      username: '',
      password: '',
      confirmPassword: '',
      phone: '',
      email: '',
      emailCode: '',
      captcha: ''
    })

    const errors = reactive({
      username: '',
      password: '',
      confirmPassword: '',
      phone: '',
      email: '',
      emailCode: '',
      captcha: ''
    })

    // 验证邮箱格式
    const isValidEmail = (email) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    }

    // 异步检查用户名是否已存在
    const usernameRegex = /^[a-zA-Z0-9]{6,18}$/
    const checkUsernameAvailability = async (force = false) => {
      const name = (registerForm.username || '').trim()
      if (!name || !usernameRegex.test(name)) return true
      try {
        const resp = await axios.get(`${serverUrl}/api/user/lookup`, {
          params: { username: name },
          validateStatus: (s) => s < 500  // 404不抛异常，避免控制台红字
        })
        if (resp.data && resp.data.success) {
          errors.username = '用户名已存在'
          return false
        }
        return true
      } catch (e) {
        if (force) {
          errors.username = '用户名校验失败，请稍后重试'
          return false
        }
        return true
      }
    }

    // 自动滚动到第一个错误字段
    const scrollToFirstError = () => {
      const fieldIdMap = ['username', 'password', 'confirmPassword', 'phone', 'email', 'emailCode']
      for (const field of fieldIdMap) {
        if (errors[field]) {
          const el = document.getElementById(field)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            el.focus()
            break
          }
        }
      }
    }

    // 用户名失焦时验证合规性（格式 + 异步查重）
    const validateUsernameOnBlur = async () => {
      const name = (registerForm.username || '').trim()
      if (!name) {
        errors.username = '请输入账号'
        return
      }
      if (!usernameRegex.test(name)) {
        errors.username = '账号只能包含大小写字母和数字，长度6-18位'
        return
      }
      // 格式通过，异步检查是否已存在
      await checkUsernameAvailability()
    }

    // 各字段失焦时验证
    const getPasswordError = (pwd) => {
      if (!pwd) return '请输入密码'
      const checks = []
      if (pwd.length < 8 || pwd.length > 16) checks.push('长度需在8-16位之间')
      if (!/[a-z]/.test(pwd)) checks.push('需包含小写字母')
      if (!/[A-Z]/.test(pwd)) checks.push('需包含大写字母')
      if (!/\d/.test(pwd)) checks.push('需包含数字')
      if (/[^\x21-\x7E]/.test(pwd)) checks.push('只允许常见英文字符、数字和特殊符号')
      return checks.length ? checks.join('，') : ''
    }
    const validateFieldOnBlur = (field) => {
      switch (field) {
        case 'password':
          errors.password = getPasswordError(registerForm.password)
          // 密码变更后也重新校验确认密码
          if (registerForm.confirmPassword) {
            if (registerForm.password !== registerForm.confirmPassword) {
              errors.confirmPassword = '两次输入的密码不一致'
            } else {
              errors.confirmPassword = ''
            }
          }
          break
        case 'confirmPassword':
          if (!registerForm.confirmPassword) {
            errors.confirmPassword = '请确认密码'
          } else if (registerForm.password !== registerForm.confirmPassword) {
            errors.confirmPassword = '两次输入的密码不一致'
          } else {
            errors.confirmPassword = ''
          }
          break
        case 'phone':
          if (!registerForm.phone) {
            errors.phone = '请输入手机号'
          } else if (!/^1[3-9]\d{9}$/.test(registerForm.phone)) {
            errors.phone = '请输入正确的手机号格式'
          } else {
            errors.phone = ''
          }
          break
        case 'email':
          if (!registerForm.email) {
            errors.email = '请输入邮箱'
          } else if (!isValidEmail(registerForm.email)) {
            errors.email = '请输入正确的邮箱格式'
          } else {
            errors.email = ''
          }
          break
      }
    }

    // 发送邮箱验证码
    const sendEmailCode = async () => {
      if (!isValidEmail(registerForm.email)) {
        errors.email = '请输入正确的邮箱格式'
        return
      }
      captchaPurpose.value = 'sendEmailCode'
      captchaVisible.value = true
    }

    const executeSendEmailCode = async (captchaToken) => {
      try {
        const response = await axios.post(`${serverUrl}/sendVerificationCode`, {
          email: registerForm.email,
          captcha: captchaToken
        })
        if (response.status === 200) {
          emailCodeCooldown.value = 300
          cooldownTimer.value = setInterval(() => {
            emailCodeCooldown.value--
            if (emailCodeCooldown.value <= 0) {
              clearInterval(cooldownTimer.value)
            }
          }, 1000)
          message.success('验证码已发送到您的邮箱，请查收')
        }
      } catch (error) {
        console.error('发送验证码失败:', error)
        if (isRateLimitError(error)) return
        errors.email = error.response?.data?.error || error.response?.data || '发送验证码失败，请稍后重试'
      }
    }

    // 表单验证
    const validateForm = () => {
      let isValid = true
      Object.keys(errors).forEach(key => { errors[key] = '' })
      
      if (!registerForm.username) {
        errors.username = '请输入账号'
        isValid = false
      } else if (!usernameRegex.test(registerForm.username)) {
        errors.username = '账号只能包含大小写字母和数字，长度6-18位'
        isValid = false
      }
      
      const pwdError = getPasswordError(registerForm.password)
      if (pwdError) {
        errors.password = pwdError
        isValid = false
      }
      
      if (!registerForm.confirmPassword) {
        errors.confirmPassword = '请确认密码'
        isValid = false
      } else if (registerForm.password !== registerForm.confirmPassword) {
        errors.confirmPassword = '两次输入的密码不一致'
        isValid = false
      }
      
      if (!registerForm.phone) {
        errors.phone = '请输入手机号'
        isValid = false
      } else if (!/^1[3-9]\d{9}$/.test(registerForm.phone)) {
        errors.phone = '请输入正确的手机号格式'
        isValid = false
      }
      
      if (!registerForm.email) {
        errors.email = '请输入邮箱'
        isValid = false
      } else if (!isValidEmail(registerForm.email)) {
        errors.email = '请输入正确的邮箱格式'
        isValid = false
      }
      
      if (!registerForm.emailCode) {
        errors.emailCode = '请输入邮箱验证码'
        isValid = false
      } else if (!/^\d{6}$/.test(registerForm.emailCode)) {
        errors.emailCode = '验证码格式不正确'
        isValid = false
      }
      
      return isValid
    }

    // 关闭模态框
    const goBack = () => {
      localStorage.removeItem('tempBindToken')
      bindToken.value = ''
      emit('close')
    }
    const switchToLogin = () => {
      localStorage.removeItem('tempBindToken')
      bindToken.value = ''
      emit('switch-to-login')
    }

    // 处理注册
    const handleRegister = async () => {
      if (!validateForm()) {
        // 收集第一个错误信息并通知用户
        const firstError = Object.values(errors).find(e => e)
        if (firstError) {
          message.error(firstError)
        }
        scrollToFirstError()
        return
      }
      
      if (!verifiedCaptchaToken.value) {
        captchaPurpose.value = 'register'
        captchaVisible.value = true
        return
      }

      isLoading.value = true
      try {
        const available = await checkUsernameAvailability(true)
        if (!available) { 
          verifiedCaptchaToken.value = ''
          return 
        }

        const verifyResponse = await axios.post(`${serverUrl}/verifyCode`, {
          email: registerForm.email,
          verificationCode: registerForm.emailCode
        })
        if (verifyResponse.status !== 200) {
          errors.emailCode = '邮箱验证码错误或已过期'
          verifiedCaptchaToken.value = ''
          return
        }

        const registerResponse = await axios.post(`${serverUrl}/api/register-user`, {
          username: registerForm.username,
          password: registerForm.password,
          phone: registerForm.phone,
          email: registerForm.email,
          captcha: verifiedCaptchaToken.value,
          bindToken: bindToken.value
        })
        if (registerResponse.data.success) {
          localStorage.removeItem('tempBindToken')
          bindToken.value = ''
          
          const userData = registerResponse.data.data
          const userToken = registerResponse.data.token || userData?.token
          if (userData && userToken) {
            try {
              localStorage.setItem('userInfo', JSON.stringify(userData))
              localStorage.setItem('userToken', userToken)
              localStorage.setItem('lastLoginTime', new Date().toISOString())
              localStorage.setItem('isLoggedIn', 'true')
            } catch (e) { console.warn('保存登录状态失败：', e) }
            message.success('注册成功，已自动登录')
            emit('close')
            setTimeout(() => { window.location.reload() }, 100)
            try {
              const redirectPath = localStorage.getItem('postLoginRedirect')
              if (redirectPath) {
                localStorage.removeItem('postLoginRedirect')
                router.push(redirectPath)
              } else {
                router.push('/')
              }
            } catch (e) {
              router.push('/')
            }
          } else {
            message.success('注册成功！请登录')
            emit('close')
          }
        } else {
          const errMsg = registerResponse.data.message || '注册失败'
          if (errMsg.includes('用户名')) {
            errors.username = errMsg
          } else if (errMsg.includes('邮箱') && !errMsg.includes('验证码')) {
            errors.email = errMsg
          } else if (errMsg.includes('手机')) {
            errors.phone = errMsg
          } else if (errMsg.includes('验证码')) {
            if (errMsg.includes('图形') || errMsg === '请输入验证码' || errMsg === '验证码错误') {
              errors.captcha = errMsg
            } else {
              errors.emailCode = errMsg
            }
          } else {
            message.error(errMsg)
          }
          // 通知用户并滚动到错误位置
          const firstError = Object.values(errors).find(e => e)
          if (firstError) {
            message.error(firstError)
            scrollToFirstError()
          }
          verifiedCaptchaToken.value = ''
        }
      } catch (error) {
        console.error('注册错误:', error)
        let errMsg = '注册失败，请检查网络连接'
        
        if (error.response?.data) {
           const data = error.response.data
           if (typeof data === 'string') {
             errMsg = data
           } else if (data.message) {
             errMsg = data.message
           } else if (data.error) {
             errMsg = data.error
           }
        }

        if (errMsg.includes('用户名') || errMsg.includes('账号')) {
          errors.username = errMsg
        } else if (errMsg.includes('邮箱') && !errMsg.includes('验证码')) {
          errors.email = errMsg
        } else if (errMsg.includes('手机')) {
          errors.phone = errMsg
        } else if (errMsg.includes('验证码')) {
          if (errMsg.includes('图形') || errMsg === '请输入验证码' || errMsg === '验证码错误') {
             errors.captcha = errMsg
          } else {
             errors.emailCode = errMsg
          }
        } else {
          message.error(errMsg)
        }
        // 通知用户并滚动到错误位置
        const firstError = Object.values(errors).find(e => e)
        if (firstError) {
          message.error(firstError)
          scrollToFirstError()
        }
        verifiedCaptchaToken.value = ''
      } finally {
        isLoading.value = false
      }
    }

    const onCaptchaSuccess = (token) => {
      if (captchaPurpose.value === 'register') {
        verifiedCaptchaToken.value = token
        handleRegister()
      } else if (captchaPurpose.value === 'sendEmailCode') {
        executeSendEmailCode(token)
      }
    }

    // 监听visible属性变化，当模态框显示时
    watch(() => props.visible, async (newVisible) => {
      if (newVisible) {
        bindToken.value = localStorage.getItem('tempBindToken') || ''
        Object.keys(errors).forEach(key => { errors[key] = '' })
        Object.keys(registerForm).forEach(key => { registerForm[key] = '' })
        emailCodeCooldown.value = 0
        if (cooldownTimer.value) {
          clearInterval(cooldownTimer.value)
          cooldownTimer.value = null
        }
        verifiedCaptchaToken.value = ''
        captchaVisible.value = false
        captchaPurpose.value = 'register'
      }
    }, { immediate: true })

    // 输入用户名时，防抖异步检查唯一性
    let usernameTimer = null
    watch(() => registerForm.username, (val) => {
      errors.username = ''
      if (usernameTimer) clearTimeout(usernameTimer)
      if (!val || !usernameRegex.test(val)) return
      usernameTimer = setTimeout(() => { checkUsernameAvailability() }, 400)
    })

    onUnmounted(() => { if (cooldownTimer.value) clearInterval(cooldownTimer.value) })

    return {
      registerForm,
      errors,
      isLoading,
      emailCodeCooldown,
      sendEmailCode,
      executeSendEmailCode,
      handleRegister,
      goBack,
      switchToLogin,
      isValidEmail,
      captchaVisible,
      verifiedCaptchaToken,
      captchaPurpose,
      onCaptchaSuccess,
      validateUsernameOnBlur,
      validateFieldOnBlur,
      showPassword,
      showConfirmPassword,
      bindToken
    }
  }
}
</script>

<style scoped>
.register-container {
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: 20px;
  box-sizing: border-box;
}

.register-overlay {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5); /* 仅透明度过度 */
}

.register-box {
  background: rgba(255, 255, 255, 0.75);
  padding: 0;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.18);
  width: 100%;
  max-width: 520px;
  position: relative;
  z-index: 10;
  overflow: hidden;
  backdrop-filter: blur(15px) saturate(180%);
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.dark-theme .register-box {
  background: rgba(28, 28, 30, 0.85);
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.45);
}

.register-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.2);
}

.dark-theme .register-header {
  background: rgba(0, 0, 0, 0.2);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

.close-btn {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  font-size: 1.2em;
  color: #86868b;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  width: 32px; height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #1d1d1f;
  transform: rotate(90deg);
}

.dark-theme .close-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #a1a1a6;
}

.dark-theme .close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.register-title {
  margin: 0;
  color: #1d1d1f;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.5px;
  font-family: '爱点风雅黑', sans-serif;
}

.dark-theme .register-title {
  color: #f5f5f7;
}

.bind-notice-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 12px;
  color: #4CAF50;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  margin-bottom: 5px;
}

.dark-theme .bind-notice-banner {
  background: rgba(76, 175, 80, 0.15);
  border-color: rgba(76, 175, 80, 0.3);
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 30px;
  overflow-y: auto;
  scrollbar-width: none;
}

.register-form::-webkit-scrollbar {
  display: none;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.form-group label {
  font-weight: 600;
  color: #424245;
  font-size: 14px;
  margin-left: 4px;
}

.dark-theme .form-group label {
  color: #a1a1a6;
}

.form-group input {
  padding: 14px 18px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.5);
  color: #1d1d1f;
  outline: none;
  box-sizing: border-box;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper input {
  width: 100%;
  padding-right: 44px;
}

.toggle-password-btn {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: #86868b;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.toggle-password-btn:hover {
  color: #1d1d1f;
}

.dark-theme .toggle-password-btn:hover {
  color: #f5f5f7;
}

.dark-theme .form-group input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f5f5f7;
}

.form-group input::placeholder {
  color: #a1a1a6;
  font-size: 14px;
}

.form-group input:focus {
  border-color: #4CAF50;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.15);
}

.dark-theme .form-group input:focus {
  border-color: #4CAF50;
  background: rgba(76, 175, 80, 0.15);
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.25);
}

.form-group input.error {
  border-color: #ff3b30;
  background: rgba(255, 59, 48, 0.05);
}

.error-message {
  color: #ff3b30;
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
  font-weight: 500;
}

.code-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.code-container input {
  flex: 1;
  min-width: 0;
}

.code-btn {
  background: rgba(76, 175, 80, 0.1);
  color: #4CAF50;
  border: none;
  padding: 0 20px;
  height: 48px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.code-btn:hover:not(:disabled) {
  background: rgba(76, 175, 80, 0.2);
  transform: translateY(-1px);
}

.dark-theme .code-btn {
  background: rgba(76, 175, 80, 0.15);
  color: #4CAF50;
}

.captcha-group .captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.captcha-group .captcha-container input {
  flex: 1;
  min-width: 0;
}

.captcha-canvas {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 48px;
  background: #fff;
}

.dark-theme .captcha-canvas {
  border-color: rgba(255, 255, 255, 0.1);
  opacity: 0.8;
}

.register-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #0071e3 100%); /* 绿蓝渐变 */
  color: white;
  border: none;
  padding: 16px 20px;
  border-radius: 14px;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 10px;
  box-shadow: 0 8px 20px rgba(76, 175, 80, 0.3);
}

.register-btn:hover:not(:disabled) {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 12px 30px rgba(76, 175, 80, 0.4);
}

.register-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.form-links {
  text-align: center;
  margin-top: 10px;
  font-size: 14px;
}

.form-links span {
  color: #86868b;
}

.login-link {
  background: none;
  border: none;
  color: #4CAF50;
  font-weight: 600;
  cursor: pointer;
  margin-left: 6px;
  text-decoration: underline;
}

.login-link:hover {
  opacity: 0.7;
}

.disclaimer-notice {
  margin-top: 10px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  text-align: center;
}

.disclaimer-notice p {
  margin: 0;
  font-size: 12px;
  color: #86868b;
  line-height: 1.6;
}

.disclaimer-link {
  color: #4CAF50;
  text-decoration: none;
  font-weight: 600;
}

.dark-theme .disclaimer-notice {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

/* 进场动画 */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-active .register-box,
.modal-leave-active .register-box {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .register-box {
  transform: scale(0.92) translateY(30px);
  opacity: 0;
}

.modal-leave-to .register-box {
  transform: scale(0.96) translateY(15px);
  opacity: 0;
}

.modal-enter-to .register-box,
.modal-leave-from .register-box {
  transform: scale(1) translateY(0);
  opacity: 1;
}

@media (max-width: 480px) {
  .register-container {
    padding: 0;
  }
  .register-box {
    border-radius: 0;
    max-height: 100vh;
    height: 100vh;
    border: none;
    max-width: 100vw;
  }
  .register-form {
    padding: 20px 15px;
  }
}
</style>
